/**
 * Profile resource: TanStack-backed read hooks for another user's public
 * profile data.
 *
 * `useProfile` is the canonical single-profile read (`profiles.get`). It
 * returns the wire `DetailedUserProfile` as-is (the resource's own shape),
 * collapsing the `hasProfile: false` placeholder to `null`.
 * `invalidateProfile` is what the `profiles.*` write paths call so an edit
 * shows without a reload.
 *
 * `useProfileComments` is the paginated `discussion-history` "Discussion
 * History" feed backed by `profiles.comments`. Visibility (moderation state,
 * the `answersVisible` privacy toggle) is enforced entirely server-side, so the
 * hook returns exactly what the caller may see — the caller's own profile
 * identity lives on `./me.ts` instead.
 */
import type { DetailedUserProfile } from '@repo/shared/orpc/procedures/profiles';
import { useMemo } from 'react';
import { useApiQueryUtils } from 'system/api-client-context';
import type { Datastore } from 'util/datastore';
import { fireAndForget } from 'util/hooks';

import { siloParamsOf } from './silo-params';
import { usePaginatedQuery } from './use-paginated-query';
import { useSiloQuery } from './use-silo-query';

export type { CommentOnSpace } from '@repo/shared/orpc/procedures/profiles';

/**
 * True when an admin name or photo override is set on this profile. The
 * pre-override `originalName`/`originalPhotoUrl` are always present for the
 * owner or an admin — they're informational, not a signal, so only the
 * override fields count. The owner/admin-only fields are absent entirely for
 * any other reader, so this is always false for them.
 */
export function profileHasOverride(profile: DetailedUserProfile | null | undefined): boolean {
    return !!(profile?.overrideName || profile?.overridePhotoUrl);
}

/**
 * One user's profile, as the wire `DetailedUserProfile`. `profile` is `null`
 * both while the request is in flight and when the key names no real profile,
 * so callers that must tell those apart — anything rendering a "no such user"
 * state — read `isLoading` too. Pass `profileKey: null` to skip the read
 * entirely (no request is issued), which is how a caller that already holds a
 * denormalized author avoids a redundant fetch.
 *
 * A `hasProfile: false` response becomes `null` rather than the placeholder.
 * `profiles.get` never 404s on an unknown key — the server synthesizes a
 * placeholder from the login-provider account, or a deleted-user stand-in when
 * even that is gone — so treating it as absent is what keeps a bogus key
 * ("admin", a deleted account) falsy for callers that branch on it. Same rule
 * `fetchProfileAsync` applies below.
 */
export function useProfile(profileKey: string | null): { profile: DetailedUserProfile | null; isLoading: boolean } {
    const utils = useApiQueryUtils();
    const { data, isLoading } = useSiloQuery({
        procedure: utils.profiles.get,
        input: { profileKey: profileKey ?? '' },
        enabled: !!profileKey,
    });
    const profile = useMemo(() => (!data || data.hasProfile === false ? null : data), [data]);
    return { profile, isLoading: !!profileKey && isLoading };
}

/**
 * One profile, read imperatively for a write path building an optimistic row
 * outside React. Goes through `QueryClient.fetchQuery` against the same
 * `profiles.get` query options `useProfile` reads, so a profile the page has
 * already rendered resolves from cache without a round-trip, and applies the
 * same `hasProfile: false` collapse.
 */
export async function fetchProfileAsync(datastore: Datastore, profileKey: string | null): Promise<DetailedUserProfile | null> {
    if (!profileKey) return null;
    const profile = await datastore
        .getQueryClient()
        .fetchQuery(datastore.getApiQueryUtils().profiles.get.queryOptions({ input: { ...siloParamsOf(datastore), profileKey } }));
    return profile.hasProfile === false ? null : profile;
}

/**
 * The admin-only, always-private read of a profile via `profiles.manage.get`.
 * Mirrors `useProfile` — a `null` key skips the request, and a
 * `hasProfile: false` collapses to `null` — but reaches the manage
 * subresource, so it carries the owner/admin-only detail (the override
 * fields, `answersVisible`, `roles`) for a profile that isn't the caller's
 * own. Only an admin may call it; the admin profile-edit surfaces are the
 * callers.
 */
export function useProfileManage(profileKey: string | null): { profile: DetailedUserProfile | null; isLoading: boolean } {
    const utils = useApiQueryUtils();
    const { data, isLoading } = useSiloQuery({
        procedure: utils.profiles.manage.get,
        input: { profileKey: profileKey ?? '' },
        enabled: !!profileKey,
    });
    const profile = useMemo(() => (!data || data.hasProfile === false ? null : data), [data]);
    return { profile, isLoading: !!profileKey && isLoading };
}

/**
 * Drop a profile's cached reads so mounted readers refetch it after a write.
 * The reads are TanStack queries, so a change to a profile only shows once its
 * cache is invalidated. Both the self read (`profiles.get`) and the admin read
 * (`profiles.manage.get`) are dropped, since an override write can change
 * whichever of the two a mounted surface is using.
 */
export function invalidateProfile(datastore: Datastore, profileKey: string) {
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    const input = { input: { siloKey: datastore.getSiloKey(), profileKey } };
    fireAndForget(queryClient.invalidateQueries({ queryKey: utils.profiles.get.key(input) }));
    fireAndForget(queryClient.invalidateQueries({ queryKey: utils.profiles.manage.get.key(input) }));
}

/**
 * A profile's own top-level comments across every space, newest first.
 * Visibility (moderation state, the `answersVisible` privacy toggle) is
 * enforced entirely server-side — the returned page is already exactly what
 * the caller is allowed to see.
 */
export function useProfileComments(profileKey: string) {
    const utils = useApiQueryUtils();
    const result = usePaginatedQuery({ procedure: utils.profiles.comments, input: { profileKey } });
    const comments = useMemo(() => result.data?.pages.flatMap((page) => page.items), [result.data]);
    return {
        comments,
        isLoading: result.isLoading,
        isFetchingNextPage: result.isFetchingNextPage,
        hasNextPage: result.hasNextPage,
        fetchNextPage: result.fetchNextPage,
    };
}
